<svelte:options customElement="hb-input-area" />

<script lang="ts">
  /**
   * Svelte Input Area WebComponent
   * =====================
   *
   * @contributors: Dario Caruso <dev@dariocaruso.info> (https://dariocaruso.info)
   *
   * @license: MIT License
   *
   */
  import type { Component } from "./types/webcomponent.type";
  import {
    parseSchemaentryProp,
    sameSetValPayload,
    schemaentryFingerprint,
    shouldApplySchemaSync,
  } from "../lib/schemaentry";

  let { schemaentry, show_validation = "no" }: Component = $props();

  let field = $state<Record<string, any> | null>(null);
  let schemaFingerprint = $state("");

  let value: string = $state("");
  let regex = $derived.by(() => {
    if (field?.validationRegex) {
      return new RegExp(field.validationRegex);
    }
    return undefined;
  });

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  let valid = $derived.by(() => {
    if (field) {
      if (field.required) {
        if (value) {
          if (regex && !regex.test(value)) {
            return false;
          } else if (field.params?.min && !(value.length >= field.params.min)) {
            return false;
          } else if (
            field?.params?.max &&
            value &&
            !(value.length <= field.params.max)
          ) {
            return false;
          } else {
            return true;
          }
        } else {
          return false;
        }
      } else {
        return true;
      }
    } else {
      return false;
    }
  });

  $effect(() => {
    const fp = schemaentryFingerprint(schemaentry);
    const nextField = parseSchemaentryProp(schemaentry);
    if (!nextField) {
      field = null;
      return;
    }
    if (shouldApplySchemaSync(fp, () => schemaFingerprint)) {
      schemaFingerprint = fp;
      field = nextField;
      if (nextField.value !== undefined && nextField.value !== null) {
        value = String(nextField.value);
      } else {
        value = "";
      }
    }
  });

  let lastSetVal = $state<{
    value: unknown;
    valid: boolean;
    id: string | undefined;
  } | null>(null);

  $effect(() => {
    const f = field;
    if (!f?.id) return;
    const payload = { value, valid, id: f.id as string | undefined };
    if (sameSetValPayload(lastSetVal, payload)) return;
    lastSetVal = payload;
    dispatch("setVal", { value, valid, id: f.id });
  });
</script>

{#if field != null}
  <div class="field">
    <div class="control">
      <textarea
        part="input"
        bind:value
        class="textarea"
        class:is-success={show_validation === "yes" &&
          !!field?.required &&
          valid}
        class:is-danger={show_validation === "yes" &&
          !!field?.required &&
          !valid}
        id={field?.id}
        required={field?.required}
        placeholder={field?.placeholder}
        readonly={field?.readonly}
        disabled={!!field?.disabled}
        onkeypress={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            dispatch("clickEnter", { value, valid, id: field?.id });
          }
        }}
      ></textarea>
    </div>

    {#if field?.validationTip && show_validation === "yes" && !valid}
      <p part="invalid-feedback" class="help is-danger">
        {field.validationTip}
      </p>
    {/if}
  </div>
{/if}

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
